/**
 * @retail-ops/shared-types/cart
 *
 * Cart state + pipeline snapshot shapes.
 * SQL backing: Carts table (§3.1), PipelineStepExecutions (§3.2), CartCifIndex (§3.12).
 */

import type { CartStatus, ArchiveReason, Origin, PipelineStepStatus } from './enums.js';
import type { Iso8601 } from './common.js';
import type { TeamView } from './followUpTasks.js';

// -----------------------------------------------------------------------------
// Wave 6.2 — Cart resolution-code workflow
// -----------------------------------------------------------------------------

/**
 * Final approved 6-code resolution taxonomy (Wave 6.3 — System-Error added).
 * Pass these EXACT strings to /v3/carts/{cartId}/resolve. Any rename breaks
 * the backend contract.
 *
 * Wave 6.3 additions:
 *   - `System-Error` — chosen by Ops when an external system failure (e.g.
 *     JxChange timeout, signing-service crash) requires manual cleanup. Valid
 *     for ALL origin × artifact tuples. Pairs with `errorCode` (closed list
 *     from `/v3/config/system-error-codes`).
 */
export type CartResolutionCode =
  | 'Open'
  | 'Processed'
  | 'Withdrawn'
  | 'Adverse-Action'
  | 'Cancelled'
  | 'System-Error';

/**
 * Origin tier classification used by the validity matrix. The companion
 * backend agent populates this on `GET /v3/carts/my` (and on the compliance
 * queue rows). UI uses it to filter the resolution-code radio set.
 */
export type CartOriginRole =
  | 'fsr-self'
  | 'lobby-queue'
  | 'lobby-entry-internal'
  | 'lobby-entry-external'
  | 'kiosk'
  | 'rcp-portal';

export interface ResolveCartInput {
  resolutionCode: CartResolutionCode;
  resolutionReason: string;
  /**
   * Wave 6.3 — required when `resolutionCode === 'System-Error'`. References
   * `SystemErrorCodes.code` (closed catalog, see /v3/config/system-error-codes).
   */
  errorCode?: string;
  /**
   * Wave 6.3 — when present and the cart contains multiple products, the
   * backend records a per-product CartProductResolution row instead of (or in
   * addition to) the cart-level resolution. Rows that match the cart-level
   * code can be omitted; only divergent dispositions need to be sent.
   */
  productResolutions?: ReadonlyArray<CartProductResolutionInput>;
}

// -----------------------------------------------------------------------------
// Wave 6.3 — Per-product disposition (CartProductResolution table)
// -----------------------------------------------------------------------------

export interface CartProductResolutionInput {
  productCode: string;
  resolutionCode: CartResolutionCode;
  /** Required when resolutionCode === 'System-Error'. */
  errorCode?: string;
  /** Optional free-form reason — falls back to the cart-level reason. */
  resolutionReason?: string;
}

export interface CartProductResolution {
  cartId: string;
  productCode: string;
  productName?: string;
  resolutionCode: CartResolutionCode;
  errorCode?: string;
  resolutionReason?: string;
  resolvedAt?: Iso8601;
  resolvedByOfficerCode?: string;
  resolvedByDisplayName?: string;
}

export interface ResolveCartResponse {
  success: boolean;
  cart?: CartDto;
  error?: string;
  /**
   * Backend-emitted code for inline-error rendering.
   * `LOBBY_TICKET_ACTIVE` / `EXTERNAL_COMMIT_LOCK` surface as inline modal
   * errors (per Wave 6.2 contract); other codes fall through to toast.
   */
  errorCode?: string;
}

export interface SweepEmptyFsrDraftsInput {
  /** Default 60. Carts with no PII + no artifact older than this are swept. */
  olderThanMinutes?: number;
  /** When true the backend returns the count without mutating any rows. */
  countOnly?: boolean;
}

export interface SweepEmptyFsrDraftsResponse {
  success: boolean;
  /** Number of carts swept (or that *would* have been swept when countOnly). */
  sweptCount: number;
  /** Cart IDs the server resolved as `Cancelled`. Omitted when countOnly. */
  resolvedCartIds?: ReadonlyArray<string>;
  error?: string;
}

export type ComplianceQueueStatus = 'pending' | 'all';

/** Wave 6.3 — entity-type filter for the Compliance queue. Default 'cart'. */
export type ComplianceQueueEntityType = 'cart' | 'maint-action' | 'all';

/**
 * Single row in the Adverse-Action review queue. Origin tier is humanized
 * upstream so the UI can render it directly (see {@link humanizeOriginTier}).
 */
export interface ComplianceQueueRow {
  cartId: string;
  branch: string;
  branchName?: string;
  /** Customer display name; `null` when fsr-self with no PII captured. */
  customerName?: string | null;
  origin: Origin;
  originRole: CartOriginRole;
  /** "Lobby" / "Branch FSR" / "Kiosk" / "Customer portal" — already humanized. */
  originTierLabel: string;
  resolvedByOfficerCode?: string;
  resolvedByDisplayName?: string;
  resolvedAt: Iso8601;
  resolutionReason: string;
  reviewStatus: 'pending' | 'in-review' | 'closed';
  /**
   * Wave 6.3 — entity-type discriminator. Default `cart` for back-compat;
   * MaintAction AA reviews surface in the same queue with `'maint-action'`.
   */
  entityType?: ComplianceQueueEntityType;
  /** Wave 6.3 — present when entityType === 'maint-action'. */
  maintActionId?: string;
  maintActionLabel?: string;
}

export interface RestoreCartResponse {
  success: boolean;
  cart?: CartDto;
  error?: string;
  /**
   * Backend-emitted code for inline-error rendering. When the resolution
   * window is exhausted the backend returns 410 Gone with `errorCode='RETENTION_WINDOW_EXPIRED'`
   * plus the contextual fields below so the modal can quote the policy.
   */
  errorCode?: string;
  /** When `errorCode='RETENTION_WINDOW_EXPIRED'` — days since the cart closed. */
  daysSinceResolved?: number;
  /** When `errorCode='RETENTION_WINDOW_EXPIRED'` — configured retention days. */
  retentionDays?: number;
  originRole?: CartOriginRole;
  resolutionCode?: CartResolutionCode;
}

// -----------------------------------------------------------------------------
// CartRetentionPolicies (Admin → System → Cart retention policies)
// PK is composite (OriginRole, ResolutionCode). Default 7 years (2555 days).
// -----------------------------------------------------------------------------

export interface CartRetentionPolicy {
  originRole: CartOriginRole;
  resolutionCode: CartResolutionCode;
  retentionDays: number;
  isActive: boolean;
  notes?: string;
  updatedAt?: Iso8601;
  updatedBy?: string;
}

export interface CartRetentionPolicyUpsertInput {
  originRole: CartOriginRole;
  resolutionCode: CartResolutionCode;
  retentionDays: number;
  isActive?: boolean;
  notes?: string;
}

// -----------------------------------------------------------------------------
// AO-DOCUMENTS-SCHEMA-REBUILD (2026-04-25) — DocumentRetentionPolicies
// PK is composite (DocumentCategory, OriginRole). Default 7 years (2555 days).
// Drives the per-row RetentionEndDate column on ao_Documents and the nightly
// retention sweep that hard-deletes expired artifacts (when IsHardHold = false).
// -----------------------------------------------------------------------------

export type AoDocumentCategory =
  | 'disclosure'
  | 'id-scan'
  | 'uploaded-doc'
  | 'signed-form'
  | 'compiled-pkg'
  | 'system-generated'
  | 'receipt-statement'
  | 'correspondence';

export interface DocumentRetentionPolicy {
  documentCategory: AoDocumentCategory;
  originRole: CartOriginRole;
  retentionDays: number;
  isActive: boolean;
  notes?: string;
  updatedAt?: Iso8601;
  updatedBy?: string;
}

export interface DocumentRetentionPolicyUpsertInput {
  documentCategory: AoDocumentCategory;
  originRole: CartOriginRole;
  retentionDays: number;
  isActive?: boolean;
  notes?: string;
}

// -----------------------------------------------------------------------------
// Cart (preserves existing `Cart` shape from prior index.ts)
// -----------------------------------------------------------------------------

/**
 * Legacy compact shape — preserved for pre-existing callers.
 * New code should use `CartDto`.
 */
export interface Cart {
  cartId: string;
  status: 'draft' | 'in-progress' | 'signing' | 'committed' | 'archived';
  customerCifId?: string;
  primaryOfficerCode?: string;
  productCodes: string[];
  createdAt: string;
  updatedAt: string;
  archivedReason?: string;
}

/**
 * Cart note — free-form FSR/supervisor notes kept with the cart.
 */
export interface CartNote {
  author: string;
  timestamp: Iso8601;
  text: string;
}

/**
 * Full v3 Cart DTO — what `/v3/carts/*` returns.
 */
export interface CartDto {
  cartId: string;
  status: CartStatus;
  origin: Origin;
  officerCode: string;
  branch: string;
  assignedUser?: string;
  lockedBySession?: string;
  lockedBySessionId?: string;
  version: number;
  createdAt: Iso8601;
  updatedAt: Iso8601;
  committedAt?: Iso8601;
  archivedAt?: Iso8601;
  archiveReason?: ArchiveReason;

  /** Pipeline state — source of truth for PipelineContext. */
  pipelineStep?: string;
  pipelineIndex?: number;
  pipelineTotalSteps?: number;
  signingGroupId?: string;
  inviteId?: string;

  /** Indexed cif refs (sourced from CartCifIndex table). */
  cifIds: ReadonlyArray<string>;
  accountIds: ReadonlyArray<string>;

  /** Serialized payloads — keep as strings on the wire. */
  peopleJson?: string;
  productsJson?: string;
  metadataJson?: string;
  pipelineSnapshotJson?: string;
  cartDataJson?: string;

  notes?: ReadonlyArray<CartNote>;
  metadata?: Record<string, unknown>;

  // ---------------------------------------------------------------------------
  // Wave 6.2 — resolution-code workflow surface
  // ---------------------------------------------------------------------------

  /**
   * Tier classification driving the resolution-code matrix. Surfaced by
   * `GET /v3/carts/my` and `GET /v3/carts/compliance-queue`.
   */
  originRole?: CartOriginRole;

  /**
   * True once a regulated artifact (signed disclosure / posted account /
   * dispatched notice) has been associated with the cart. Drives the matrix:
   *   - fsr-self + false  → only Cancelled valid
   *   - fsr-self + true   → Withdrawn / Adverse-Action / Processed
   */
  hasRegulatedArtifact?: boolean;

  /** Terminal resolution once /v3/carts/{cartId}/resolve has been called. */
  resolutionCode?: CartResolutionCode;
  resolvedAt?: Iso8601;
  resolvedByOfficerCode?: string;
  resolutionReason?: string;
  /** Wave 6.3 — optional system error code (set when resolutionCode='System-Error'). */
  errorCode?: string;
  /** Wave 6.3 — true when this cart was created in test-mode. Drives the TEST-MODE badge. */
  isTestMode?: boolean;

  /**
   * Wave 6.3 — when populated, per-product dispositions diverge from the
   * cart-level code. Surfaced inline on the FSR card and on the metrics
   * mixed-disposition panel.
   */
  productResolutions?: ReadonlyArray<CartProductResolution>;

  /** Wave 6.3 — current retention hold (when present). */
  retentionHold?: RetentionHold;

  // ---------------------------------------------------------------------------
  // Wave 7 — Commit pipeline timing surface (per §6.1 of the design doc)
  // ---------------------------------------------------------------------------

  /** Wave 7 — first commit attempt timestamp. Drives "Submitted N min ago" pill. */
  commitStartedAt?: Iso8601;
  /** Wave 7 — terminal commit-completed timestamp. */
  commitCompletedAt?: Iso8601;
  /** Wave 7 — when the cart entered the awaiting-signatures pause. Drives the 14d expiry countdown on the cart card. */
  awaitingSignaturesAt?: Iso8601;
  /** Wave 7 — last pipeline-error timestamp. Drives the "Needs attention" recency pill. */
  lastPipelineErrorAt?: Iso8601;
  /** Wave 7 — humanized error message attached to the most recent pipeline-error transition. */
  lastPipelineErrorMsg?: string;
  /** Wave 7 — origin role snapshot at commit start (immutable once stamped). */
  commitOrigin?: CartOriginRole;
  /** Wave 7 — number of commit attempts so far (manual retry increments). */
  pipelineAttemptCount?: number;

  // ─────────────────────────────────────────────────────────────────────────
  // Bill 2026-05-08 — ComplianceQuestion kill-plan Phase 2 + MSB manager
  // approval + Risk-review surface. These are cart-level columns on the
  // Cart entity exposed verbatim through CartDto so the Review-stage roll-up
  // (and downstream FSR cards) can render without a second round-trip.
  // ─────────────────────────────────────────────────────────────────────────
  purpose?: string;
  sourceOfFunds?: string;
  expectedCashActivity?: string;

  /** True when an MSB manager approved this cart for opening. */
  msbManagerApproval?: boolean;
  msbManagerApprovalBy?: string;
  msbManagerApprovalAt?: Iso8601;

  /** Pre-AcctAdd Risk Mgmt review decision (Irrevocable Trust / SNT / etc.). */
  riskReviewStatus?: 'NotRequired' | 'Pending' | 'Approved' | 'Rejected' | 'Returned';
  riskReviewDecidedBy?: string;
  riskReviewDecidedAt?: Iso8601;
  riskReviewComment?: string;

  /** Cart mode (Production / Pilot-Live / Test-Live / Sandbox). */
  /** What the cart is for: 'Opening' (older rows: the dormant 'Production') or 'Maintenance' (Bill 2026-10-17). */
  mode?: 'Production' | 'Pilot-Live' | 'Test-Live' | 'Sandbox' | 'Opening' | 'Maintenance';
}

/**
 * Per-product snapshot rendered on cart cards. Subset of the AccountSpec /
 * AddToCartModal commit payload; ProductsStep / OwnershipStep read this off
 * `cart.productsJson` (which is a JSON-as-string of `CartSelectedProduct[]`).
 *
 * Mirrors CartFunction.CartPatchInput.ProductsJson schema.
 */
export interface CartSelectedProduct {
  code: string;
  /**
   * Row-store blueprint P2 — per-instance identity. FE generates
   * `"li-" + crypto.randomUUID()` for NEW instances; legacy items carry
   * `instanceId === productCode` (set by backfill). Composed productsJson
   * entries now ALWAYS carry this key.
   */
  instanceId?: string;
  qty: number;
  openingDeposit?: number;
  cardTypeCode?: string;
  primaryOwnerId?: string;
  jointOwnerIds?: ReadonlyArray<string>;
  beneficiaryIds?: ReadonlyArray<string>;
  featureValues?: Record<string, unknown>;
}

// -----------------------------------------------------------------------------
// Row-store blueprint P2 — cart-content command inputs + envelope
// (docs/architecture/cart-row-store-blueprint-20260706.md)
//
// Content domains move from whole-blob PATCHes to targeted row commands:
//   POST   /v3/carts/{cartId}/parties
//   PATCH  /v3/carts/{cartId}/parties/{partyKey}      (partyKey = clientId OR partyId GUID)
//   DELETE /v3/carts/{cartId}/parties/{partyKey}
//   POST   /v3/carts/{cartId}/line-items              (ALWAYS creates a NEW instance)
//   PATCH  /v3/carts/{cartId}/line-items/{instanceId}
//   DELETE /v3/carts/{cartId}/line-items/{instanceId}
//   PUT    /v3/carts/{cartId}/line-items/{instanceId}/beneficiaries  (replace-set)
// After every command the server recomposes peopleJson/productsJson/metadataJson,
// so GET /v3/carts/{id} and all legacy readers stay correct.
// -----------------------------------------------------------------------------

/**
 * Body for POST/PATCH `/v3/carts/{cartId}/parties[/{partyKey}]`.
 *
 * `person` carries the SAME keys as a peopleJson person object
 * (`{ id, role, mode, cifId, firstName..., dob:"yyyy-MM-dd", extra:{...} }`);
 * the server maps them onto the CartParties row and recomposes the blob.
 */
export interface CartPartyUpsertInput {
  /**
   * FE client id ("person-{uuid}") — optional on create (server generates one
   * when omitted); ignored on PATCH (the partyKey path segment identifies the
   * row). Existing FE ids keep working — server correlates via CartParties.ClientId.
   */
  clientId?: string;
  /** Person payload — same keys as a peopleJson person object. */
  person: Record<string, unknown>;
  /**
   * Per-ROW optimistic concurrency (PATCH only — NOT the cart-level version).
   * When provided and stale the server rejects with
   * `409 ROW_VERSION_MISMATCH`. Commands NEVER require cart-level expectedVersion.
   */
  expectedRowVersion?: number;
}

/**
 * Body for POST `/v3/carts/{cartId}/line-items`. ALWAYS creates a NEW
 * instance — never upserts by product code (two Free Checkings = two rows).
 */
export interface CartLineItemCreateInput {
  /**
   * Per-instance identity — FE generates `"li-" + crypto.randomUUID()`.
   * Optional; server generates one when omitted. Legacy items have
   * `instanceId === productCode` (set by backfill).
   */
  instanceId?: string;
  /** Catalog key (dbo.Products.Code). */
  productCode: string;
  /** Services/features/funding config for THIS instance (ConfigJson). */
  config?: Record<string, unknown>;
  primaryOwnerClientId?: string;
  jointOwnerClientIds?: ReadonlyArray<string>;
  /** Optional FSR label (e.g. "Vacation savings"). */
  nickname?: string;
}

/**
 * Body for PATCH `/v3/carts/{cartId}/line-items/{instanceId}`. All fields
 * optional — only supplied keys are applied to the row.
 */
export interface CartLineItemPatchInput {
  config?: Record<string, unknown>;
  primaryOwnerClientId?: string;
  jointOwnerClientIds?: ReadonlyArray<string>;
  nickname?: string;
  /**
   * Per-ROW optimistic concurrency — `409 ROW_VERSION_MISMATCH` when provided
   * and stale. Never the cart-level version.
   */
  expectedRowVersion?: number;
}

/**
 * Envelope returned by EVERY cart-content command route (parties, line-items,
 * beneficiaries). `cart` is the full recomposed CartDto projection — same
 * shape as GET `/v3/carts/{id}` — so callers can replace local state without
 * a second round-trip. Conflict: HTTP 409 with
 * `errorCode='ROW_VERSION_MISMATCH'` ONLY when expectedRowVersion was
 * provided and stale.
 */
export interface CartContentCommandResult {
  success: boolean;
  /** Full CartDto projection recomposed after the command. */
  cart: CartDto;
  /** The affected CartParties row projection (party commands). */
  party?: Record<string, unknown>;
  /** The affected CartLineItems row projection (line-item commands). */
  lineItem?: Record<string, unknown>;
  error?: string;
  errorCode?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tier 2 — Review-stage SPFx-aligned types (Bill 2026-05-12 Round 3).
// Powers /v3/carts/{cartId}/review-summary + /preview-pipeline + /decline.
// ─────────────────────────────────────────────────────────────────────────────

export interface ReviewSummaryChip {
  chipKey: string;
  displayLabel: string;
  displayOrder: number;
  description: string | null;
}

export interface ResolvedGateBadge {
  gateKey: string;
  strategy: string;
  deferralDays: number | null;
  citationKey: string;
  isSubtypeOverride: boolean;
}

export interface ReviewSubtypeGates {
  customerType: string;
  gates: ReadonlyArray<ResolvedGateBadge>;
}

export interface ReviewSummary {
  cartId: string;
  cartStatus: string;
  pipelineStep: string | null;
  pipelineStepIndex: number | null;
  chips: ReadonlyArray<ReviewSummaryChip>;
  subtypeGates: ReadonlyArray<ReviewSubtypeGates>;
  pendingDeferrals: number;
  generatedAt: Iso8601;
}

export interface PipelineStageDescriptor {
  stage: string;
  label: string;
  kind: 'preflight' | 'pause-signing' | 'post-sign';
}

export interface PipelinePreviewFinding {
  customerType: string;
  wouldBlock: ReadonlyArray<{ gateKey: string; citationKey: string }>;
  wouldWarn:  ReadonlyArray<{ gateKey: string; citationKey: string }>;
  wouldDefer: ReadonlyArray<{ gateKey: string; deferralDays: number | null; citationKey: string }>;
  officerOver: ReadonlyArray<{ gateKey: string; citationKey: string }>;
}

export interface PipelinePreview {
  cartId: string;
  cartStatus: string;
  stages: ReadonlyArray<PipelineStageDescriptor>;
  findings: ReadonlyArray<PipelinePreviewFinding>;
  /**
   * BACKLOG-1787 — the FraudPoint gate exactly as commit will run it, active
   * supervisor overrides included. Null when the cart has no people.
   */
  fraudPointGate?: {
    blocked: boolean;
    blockedReasons?: ReadonlyArray<string>;
    warnings?: ReadonlyArray<string>;
    supervisorOverrides?: ReadonlyArray<{ personIndex: number; personName: string; overrideId: string; grantedBy: string }>;
    error?: string;
  } | null;
  generatedAt: Iso8601;
}

// -----------------------------------------------------------------------------
// BACKLOG-1787 — Supervisor overrides (/v3/carts/{cartId}/supervisor-overrides)
// -----------------------------------------------------------------------------

/** Gate keys whose server-side evaluation honours a supervisor override. */
export type SupervisorOverrideGateKey = 'FraudPointRisk';

/** One ledger row (dbo.SupervisorOverrides). Never deleted; a revoke stamps it. */
export interface SupervisorOverride {
  overrideId: string;
  cartId: string;
  /** The cart person (peopleJson person.id) the override is for. */
  partyId: string | null;
  gateKey: string;
  grantedByUpn: string;
  grantedByOfficerCode: string | null;
  reason: string;
  createdAt: Iso8601;
  revokedAt: Iso8601 | null;
  revokedBy: string | null;
  revokeReason: string | null;
  isActive: boolean;
}

/** GET response: the ledger plus what the signed-in user may do (server-decided). */
export interface SupervisorOverrideList {
  success: boolean;
  cartId: string;
  /** Role codes from SystemSetting `override.supervisor.roles` (default ["Supervisor"]). */
  allowedRoles: ReadonlyArray<string>;
  canGrant: boolean;
  /** Plain-language reason when canGrant is false (wrong role, own application, closed cart). */
  cannotGrantReason: string | null;
  canRevoke: boolean;
  overrides: ReadonlyArray<SupervisorOverride>;
}

export interface SupervisorOverrideGrantInput {
  gateKey: SupervisorOverrideGateKey;
  partyId?: string;
  reason: string;
}

export interface SupervisorOverrideResult {
  success: boolean;
  data?: SupervisorOverride;
  code?: string;
  error?: string;
}

export type CartDeclineReason = 'Adverse' | 'Cancelled' | 'Withdrew';

export interface CartDeclineInput {
  declineReason: CartDeclineReason;
  details?: string;
  officerCode?: string;
}

// -----------------------------------------------------------------------------
// Wave 6.3 — Retention holds
// -----------------------------------------------------------------------------

export interface RetentionHold {
  cartId: string;
  extendedTo: Iso8601;
  reason: string;
  placedAt: Iso8601;
  placedByOfficerCode?: string;
  placedByDisplayName?: string;
}

export interface RetentionHoldRequest {
  cartId: string;
  /** ISO datetime when the hold expires. Must be > current retention end. */
  extendedTo: Iso8601;
  reason: string;
}

/**
 * Teams-app-wiring §3.2 alias.
 */
export type CartStateDto = CartDto;

/**
 * Envelope returned by `POST/GET/PATCH /v3/carts/*`.
 */
export interface CartStateResult {
  success: boolean;
  cart?: CartDto;
  error?: string;
  errorCode?: string;
}

// -----------------------------------------------------------------------------
// Cart inputs
// -----------------------------------------------------------------------------

export interface CreateCartPayload {
  origin: Origin;
  officerCode: string;
  branch: string;
  assignedUser?: string;
  cartDataJson?: string;
  peopleJson?: string;
  productsJson?: string;
  metadataJson?: string;
  isTestMode?: boolean;
}

export type CartCreateInput = CreateCartPayload;

export interface UpdateCartPayload {
  expectedVersion?: number;
  status?: CartStatus;
  peopleJson?: string;
  productsJson?: string;
  pipelineSnapshotJson?: string;
  cartDataJson?: string;
  metadataJson?: string;
  pipelineStep?: string;
  pipelineIndex?: number;
  pipelineTotalSteps?: number;
  signingGroupId?: string;
  inviteId?: string;
}

export type CartPatchInput = UpdateCartPayload;

export interface CartClaimInput {
  sessionId: string;
  officerCode: string;
}

export interface CartArchiveInput {
  reason: ArchiveReason;
  details?: string;
  notes?: string;
}

export interface CartListFilters {
  /**
   * `all=1` on /v3/carts/my (2026-09-11). Server-scoped by CartScopePredicateService
   * from the caller's role: Admin/SuperAdmin see every cart, Managers their region,
   * Supervisors their groups; an Officer gets exactly the "mine" list. Widens nothing
   * by itself. Superseded by `view` (2026-10-16); the server reads all=1 as view=all.
   */
  all?: boolean;
  /**
   * Home's Mine | My team | All (Bill 2026-10-16). Omit for "mine". The server decides from the
   * sign-in whether the caller may use the view; if not it returns their own carts and
   * `team.hasTeam = false`.
   */
  view?: TeamView;
  officerCode?: string;
  branch?: string;
  status?: CartStatus;
  origin?: Origin;
  top?: number;
  since?: Iso8601;
}

// -----------------------------------------------------------------------------
// Pipeline snapshot (stored JSON-encoded in Carts.pipelineSnapshotJson)
// -----------------------------------------------------------------------------

export interface PipelineSnapshot {
  pipelineName: string;
  version: number;
  currentStep: string;
  currentIndex: number;
  totalSteps: number;
  startedAt: Iso8601;
  lastAdvancedAt: Iso8601;
  steps: ReadonlyArray<PipelineStepExecution>;
  /** Deep context replayed by PipelineResumeFunction. */
  context?: Record<string, unknown>;
}

export interface PipelineStepExecution {
  stepCode: string;
  stepOrder: number;
  status: PipelineStepStatus;
  startedAt?: Iso8601;
  endedAt?: Iso8601;
  elapsedMs?: number;
  attempts?: number;
  lastError?: string;
  httpStatus?: number;
  /** Opaque payload for this step (e.g. AcctAddResponse). */
  result?: unknown;
}

// -----------------------------------------------------------------------------
// Wave 6.3 — SuperAdmin Records Management surface
// -----------------------------------------------------------------------------

/**
 * Closed catalog row — surfaces in the hard-delete reason picker. Backed by
 * `dbo.PurgeReasonCodes` (Wave 6.3 backend agent).
 */
export interface PurgeReasonCode {
  code: string;
  label: string;
  description?: string;
  /** Plain-language guidance shown beneath the radio in the hard-delete dialog. */
  helperText?: string;
  isActive: boolean;
  sortOrder?: number;
  updatedAt?: Iso8601;
}

export interface PurgeReasonCodeUpsertInput {
  code: string;
  label: string;
  description?: string;
  helperText?: string;
  isActive?: boolean;
  sortOrder?: number;
}

/**
 * Closed catalog row — error codes the FSR can pair with `System-Error`
 * resolution. Backed by `dbo.SystemErrorCodes`.
 */
export interface SystemErrorCode {
  code: string;
  label: string;
  /** Sourcing system (jXchange, Signing, Twain, AzureFunction, etc.). */
  sourceSystem?: string;
  description?: string;
  /** Suggested next-action shown in the modal hint. */
  suggestedNextAction?: string;
  isActive: boolean;
  sortOrder?: number;
  updatedAt?: Iso8601;
}

export interface SystemErrorCodeUpsertInput {
  code: string;
  label: string;
  sourceSystem?: string;
  description?: string;
  suggestedNextAction?: string;
  isActive?: boolean;
  sortOrder?: number;
}

/** Entity types eligible for SuperAdmin records management. */
export type PurgeEntityType = 'Cart' | 'CartProduct' | 'MaintAction';

/** Single row from `GET /v3/super-admin/records/purge-history`. */
export interface PurgeHistoryRow {
  purgeHistoryId: number;
  purgedAt: Iso8601;
  purgedByOfficerCode?: string;
  purgedByDisplayName?: string;
  entityType: PurgeEntityType;
  entityKey: string;
  /** Humanized form of `entityKey` (e.g., "Open cart for Smith, J — 2 products"). */
  entityKeyHumanized?: string;
  reasonCode: string;
  /** Humanized via PurgeReasonCodes.label. */
  reasonCodeLabel?: string;
  reason: string;
  /** Path inside `ao_PurgeRecords` SP library where the JSON snapshot lives. */
  snapshotPath?: string;
  /** Whether the underlying record has been restored since (idempotent flag). */
  isRecovered?: boolean;
  recoveredAt?: Iso8601;
  recoveredByOfficerCode?: string;
}

/** Filter shape for `GET /v3/super-admin/records/browse`. */
export interface RecordsBrowseFilters {
  origin?: Origin;
  resolution?: CartResolutionCode;
  entityType?: PurgeEntityType;
  /** Cutoff in days — only records older than this are returned. */
  ageDays?: number;
  hasArtifact?: boolean;
  isTestMode?: boolean;
  officerCode?: string;
  branch?: string;
  /** Substring search across humanized customer name. */
  customerNameQuery?: string;
  top?: number;
}

export interface RecordsBrowseRow {
  entityType: PurgeEntityType;
  entityKey: string;
  /** Humanized identity (no GUIDs). */
  displayLabel: string;
  customerName?: string | null;
  branch?: string;
  branchName?: string;
  officerCode?: string;
  officerDisplayName?: string;
  origin?: Origin;
  originRole?: CartOriginRole;
  resolutionCode?: CartResolutionCode;
  resolvedAt?: Iso8601;
  hasRegulatedArtifact?: boolean;
  isTestMode?: boolean;
  ageDays: number;
  retentionEndsAt?: Iso8601;
  hasRetentionHold?: boolean;
}

/** Body for `POST /v3/super-admin/records/purge`. */
export interface RecordsHardDeleteInput {
  entityType: PurgeEntityType;
  entityKey: string;
  reasonCode: string;
  reason: string;
}

export interface RecordsHardDeleteResponse {
  success: boolean;
  purgeHistoryId?: number;
  snapshotPath?: string;
  error?: string;
  errorCode?: string;
}

/** Body for `POST /v3/super-admin/records/purge-bulk`. */
export interface RecordsHardDeleteBulkInput {
  records: ReadonlyArray<{ entityType: PurgeEntityType; entityKey: string }>;
  reasonCode: string;
  reason: string;
}

/** Body for `POST /v3/super-admin/records/correct`. */
export interface RecordsBulkCorrectInput {
  records: ReadonlyArray<{ entityType: PurgeEntityType; entityKey: string }>;
  /**
   * Field-by-field corrections. Backend rejects keys not in its allowlist
   * (originRole is NEVER mutable — enforced server-side too).
   */
  corrections: {
    assignedOfficerCode?: string;
    branchCode?: string;
    resolutionCode?: CartResolutionCode;
    resolutionReason?: string;
    /** ISO datetime override for retention end. */
    retentionEndsAt?: Iso8601;
  };
}

export interface RecordsBulkCorrectResponse {
  success: boolean;
  affectedCount?: number;
  error?: string;
}

/** Body for `POST /v3/super-admin/records/test-mode-wipe`. */
export interface RecordsTestModeWipeInput {
  /** Required confirmation phrase — backend rejects anything but `DELETE TEST DATA`. */
  confirmation: 'DELETE TEST DATA';
}

export interface RecordsTestModeWipeResponse {
  success: boolean;
  /** Aggregate counts only — per Bill's "no recording" rule, no per-officer/branch breakdown. */
  cartsWiped?: number;
  cartProductsWiped?: number;
  maintActionsWiped?: number;
  error?: string;
}

export interface RecordsRecoveryFilters {
  startDate?: Iso8601;
  endDate?: Iso8601;
  actorOfficerCode?: string;
  entityType?: PurgeEntityType;
  reasonCode?: string;
  hasTestMode?: boolean;
  customerNameQuery?: string;
  top?: number;
}

export interface RecordsRecoveryRow extends PurgeHistoryRow {
  /** Pre-parsed snapshot summary so the UI doesn't have to fetch the JSON. */
  snapshotSummary?: {
    customerName?: string | null;
    branch?: string;
    productCodes?: ReadonlyArray<string>;
    isTestMode?: boolean;
  };
}

export interface RecoverFromSnapshotInput {
  purgeHistoryId: number;
}

export interface RecoverFromSnapshotResponse {
  success: boolean;
  restoredEntityType?: PurgeEntityType;
  restoredEntityKey?: string;
  error?: string;
  errorCode?: string;
}

// -----------------------------------------------------------------------------
// Wave 6.3 — MaintAction resolution surface
// -----------------------------------------------------------------------------

/**
 * Compact representation of a Maintenance action card. Same 6-code resolution
 * taxonomy as Carts; backend wires the row to /v3/maint-actions/{id}/resolve.
 */
export interface MaintActionResolutionDto {
  maintActionId: string;
  /** Plain-language action title (e.g., "Address change for Smith, J"). */
  displayLabel: string;
  customerName?: string;
  cifId?: string;
  acctId?: string;
  branchCode?: string;
  branchName?: string;
  officerCode?: string;
  officerDisplayName?: string;
  /** Action code from RAM_ACTIONS (address-change, card-reissue, etc.). */
  actionCode: string;
  status: 'open' | 'pending' | 'resolved';
  /** Wave 6.3 — resolution code once /v3/maint-actions/{id}/resolve fires. */
  resolutionCode?: CartResolutionCode;
  resolutionReason?: string;
  errorCode?: string;
  resolvedAt?: Iso8601;
  resolvedByOfficerCode?: string;
  resolvedByDisplayName?: string;
  hasRegulatedArtifact?: boolean;
  isTestMode?: boolean;
  createdAt: Iso8601;
}

export interface ResolveMaintActionInput {
  resolutionCode: CartResolutionCode;
  resolutionReason: string;
  errorCode?: string;
}

export interface ResolveMaintActionResponse {
  success: boolean;
  maintAction?: MaintActionResolutionDto;
  error?: string;
  errorCode?: string;
}

// -----------------------------------------------------------------------------
// Wave 10 — RAM/RCM Maintenance Action Completeness (A5 BLOCKER)
// -----------------------------------------------------------------------------

/**
 * Wire-shape for an attachment record passed inline on a maint-action submit
 * request. Resolves to a row in `dbo.MaintActionAttachments` server-side.
 */
export interface MaintActionAttachmentInput {
  /** Closed taxonomy — see backend Wave 10 design doc §6.3. */
  attachmentKind:
    | 'death-cert'
    | 'court-order'
    | 'marriage-cert'
    | 'id-front'
    | 'id-back'
    | 'signature-card'
    | 'ssn-card'
    | 'birth-cert'
    | 'tax-form'
    | 'poa-document'
    | 'utility-bill'
    | 'other';
  spDocumentUrl: string;
  spDocumentItemId?: string;
  notes?: string;
}

/** Standard envelope returned by every /v3/maint/* submit route. */
export interface MaintActionSubmitResponse {
  maintActionId: string;
  actionType: string;
  status:
    | 'in-progress'
    | 'awaiting-approval'
    | 'committed'
    | 'pipeline-error';
  approvalRequired: boolean;
  approverRole?: string;
  approvalScenario?: string;
  graphApprovalId?: string;
  jxchangeStatusCode?: number;
  testModeSkipped: boolean;
  note?: string;
}

// ── Account-level (RAM) DTOs ────────────────────────────────────────────────

export interface ChangeAccountTitleInput {
  accountNumber: string;
  newTitle: string;
  reason?: string;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeAccountOwnershipTypeInput {
  accountNumber: string;
  newCallClass: string;
  reason?: string;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeOdProtectionInput {
  accountNumber: string;
  odPrvlgOptVal?: string;
  odProtAmt?: number;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeOdLimitInput {
  accountNumber: string;
  newLimitAmount: number;
  priorLimitAmount?: number;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeStatementPrefsInput {
  accountNumber: string;
  eStatement: boolean;
  paperStatement: boolean;
  frequency?: string;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeAccountFeaturesInput {
  accountNumber: string;
  featuresAdded?: ReadonlyArray<string>;
  featuresRemoved?: ReadonlyArray<string>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface BlockUnblockAccountInput {
  accountNumber: string;
  action: 'block' | 'unblock';
  statusCode?: string;
  reason?: string;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface CloseAccountInput {
  accountNumber: string;
  mode: 'graceful' | 'force';
  reason?: string;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ReactivateDormantInput {
  accountNumber: string;
  lastContactDate?: Iso8601;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeRateIndexInput {
  accountNumber: string;
  newRateIndex: string;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

// ── Customer-level (RCM) DTOs ───────────────────────────────────────────────

export interface ChangeCifNameInput {
  cifId: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  perName?: string;
  supportingDocType: 'court-order' | 'marriage-cert' | 'divorce-decree' | 'other';
  supportingDocId?: string;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeCifDobInput {
  cifId: string;
  newBirthDate: Iso8601;
  priorBirthDate?: Iso8601;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeCifTaxIdInput {
  cifId: string;
  newTaxId: string;
  priorTaxId?: string;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface CifAddressBlockInput {
  streetAddr1?: string;
  streetAddr2?: string;
  city?: string;
  state?: string;
  zip?: string;
  mailAddr?: string;
  mailCity?: string;
  mailState?: string;
  mailZip?: string;
}

export interface CifPhonePrefInput {
  phoneType?: string;
  number?: string;
}

export interface ChangeCifContactInput {
  cifId: string;
  addressBlock?: CifAddressBlockInput;
  phones?: ReadonlyArray<CifPhonePrefInput>;
  emails?: ReadonlyArray<string>;
  cascadeAccountNumbers?: ReadonlyArray<string>;
  authMethod?: 'InPerson' | 'Phone' | 'Mail' | 'Online';
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeChannelPreferencesInput {
  cifId: string;
  allowEmail?: boolean;
  allowSms?: boolean;
  allowMail?: boolean;
  allowPhone?: boolean;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ChangeEmployerOccupationInput {
  cifId: string;
  employer?: string;
  occupation?: string;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface IdDocumentRefreshInput {
  cifId: string;
  idType: string;
  idNumber: string;
  idState?: string;
  idIssueDate?: Iso8601;
  idExpirationDate?: Iso8601;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface ForeignStatusUpdateInput {
  cifId: string;
  ssnObtained: boolean;
  newTaxId?: string;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface MarkDeceasedInput {
  cifId: string;
  deathDate: Iso8601;
  deathCertOnFile: boolean;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface OfacRescreenInput {
  cifId: string;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

// ── Cross-action DTOs ──────────────────────────────────────────────────────

export interface JointOwnerAddInput {
  accountNumber: string;
  jointCifId?: string;
  jointFirstName?: string;
  jointLastName?: string;
  jointTaxId?: string;
  jointBirthDate?: Iso8601;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface JointOwnerRemoveInput {
  accountNumber: string;
  jointCifId: string;
  supportingDocs?: ReadonlyArray<MaintActionAttachmentInput>;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

export interface AddPodBeneficiaryInput {
  accountNumber: string;
  cifId: string;
  beneficiaryName: string;
  beneficiaryTaxId?: string;
  beneficiaryBirthDate?: Iso8601;
  relationship?: string;
  reason?: string;
  isTestMode?: boolean;
  triggerCartId?: string;
}

// -----------------------------------------------------------------------------
// Wave 7 — Cart commit pipeline (RAO §3.1 11-step orchestrator)
// -----------------------------------------------------------------------------

/**
 * Canonical 12-step commit-pipeline keys (Wave 0, 2026-04-27).
 *
 * Wave 0 split `account-create` into two distinct steps to enforce Bill's
 * locked order CIFs → AcctIdGen → Sign → AcctAdd:
 *   - `acct-id-gen` (pre-sign) allocates Core Director account IDs without
 *     posting accounts. Disclosures render with real account numbers.
 *   - `acct-add` (post-sign) actually posts accounts to Core using the
 *     pre-allocated IDs after signatures complete.
 * `beneficiary-attach` moved from pre-sign to post-sign because beneficiary
 * attachment requires a posted Core account.
 *
 * Backend step-name aliases (sent on the wire by CartFunction commit-status):
 *   `validate`            → `pre-commit-validation`
 *   `acct-id-gen`         → `acct-id-gen`
 *   `disclosure-prefill`  → `disclosure-pre-fill`
 *   `signnow-dispatch`    → `signing-dispatch`
 *   `acct-add`            → `acct-add`
 *   `post-sign-jxchange`  → `post-sign-jx`
 *   `resolution`          → `cart-resolution-stamp`
 *
 * Legacy alias `account-create` is retained in the union for back-compat with
 * any in-flight carts created under the 11-step shape. The label humanizer
 * still maps it.
 */
export type CommitPipelineStepKey =
  | 'pre-commit-validation'
  | 'cif-create'
  | 'acct-id-gen'
  | 'disclosure-pre-fill'
  | 'signing-dispatch'
  | 'await-signatures'
  | 'acct-add'
  | 'beneficiary-attach'
  | 'post-sign-jx'
  | 'qc-dataverse-write'
  | 'notifications'
  | 'cart-resolution-stamp'
  /** Wave-0 deprecated — old 11-step shape; mapped at render time. */
  | 'account-create';

/**
 * Per-step status in the live progress poll.
 *
 * `paused` is reserved for `await-signatures` — the only step that can pause
 * the entire pipeline waiting on an external (customer) action.
 */
export type CommitPipelineStepStatus =
  | 'pending'
  | 'in-progress'
  | 'success'
  | 'failed'
  | 'paused'
  | 'skipped';

/**
 * One step in the live progress payload returned by /commit/status.
 * Mirrors a partial projection of `PipelineStepExecutions` (§6.2).
 */
export interface CommitPipelineStep {
  stepKey: CommitPipelineStepKey;
  /** 1-11 ordinal for stable rendering. */
  stepIndex: number;
  status: CommitPipelineStepStatus;
  startedAt?: Iso8601;
  completedAt?: Iso8601;
  /** Closed-list error code (joins SystemErrorCodes catalog when status='failed'). */
  errorCode?: string;
  /** Humanized error message; never raw exception text. */
  errorMessage?: string;
  /**
   * Optional per-step label override — when the server can say what ACTUALLY
   * happened, it wins over the static commitStepLabel(stepKey). Today only
   * cif-create sets it ("(2) Customer records updated", "(1) created, (1)
   * updated") so the tracker stops claiming "created" when CIFs were updated.
   */
  detailLabel?: string;
}

/**
 * One opened account in the completion confirmation record
 * (Bill smoke-test round 2, 2026-07-10). Account numbers are FULL — the
 * FSR files them; masking is a UI-display concern this record never applies.
 */
export interface CompletionSummaryAccount {
  /** Core Director account id, full. */
  acctId: string;
  /** Core account type (D/S/T vocabulary from AcctIdGen). */
  acctType?: string;
  /** v3 Products.Code — join to config.products for the display name. */
  productCode?: string;
}

/**
 * Completion confirmation record persisted at commit finalize
 * (PipelineResumeFunction.PersistCompletionRecordAsync →
 * Cart.MetadataJson.completionSummary) and surfaced on
 * `GET /v3/carts/{cartId}/commit/status`. Drives the post-commit
 * "what actually happened" panel so the FSR is never left guessing
 * (Bill smoke-test round 2, 2026-07-10 — "left high and dry").
 */
export interface CartCompletionSummary {
  accountsCreated?: ReadonlyArray<CompletionSummaryAccount>;
  /** Every CIF on the application (created + updated), full ids. */
  cifs?: ReadonlyArray<string>;
  /** CIFs the pipeline created (incl. adopted-existing). */
  cifsCreated?: ReadonlyArray<string>;
  /** Existing CIFs the pipeline updated. */
  cifsUpdated?: ReadonlyArray<string>;
  /** Distinct signature-captured documents (ESIGN consent excluded). */
  docsSigned?: number;
  committedAt?: Iso8601;
  /** POD/IRA beneficiaries wired at commit (Bill 2026-07-13 Item 31). */
  beneficiaries?: ReadonlyArray<{
    name?: string;
    relationship?: string | null;
    role?: string;
    percent?: number | null;
    cifId?: string | null;
    lineKey?: string;
  }>;
  /** Debit cards issued by the pipeline (masked-free summary refs). */
  cards?: ReadonlyArray<{ cifId?: string; acctId?: string; embosName?: string }>;
  /** Per-line selected services (debit card / checks / e-stmts / IB / ODP). */
  services?: ReadonlyArray<{
    productCode?: string | null;
    acctId?: string | null;
    services?: ReadonlyArray<string>;
  }>;
  /** Bill 2026-10-02 — required signers (every signer × every signable document). */
  signaturesRequired?: number;
  /** Signers whose signatures are all captured. */
  signaturesCaptured?: number;
  /** Signers still unsigned; `remote` = a remote link is out (its own remote-signature task). */
  signersOutstanding?: ReadonlyArray<{
    signerIndex: number;
    name: string;
    role: string;
    remote?: boolean;
    cifId?: string | null;
    email?: string | null;
  }>;
}

/**
 * Live status snapshot for the CommitFlow progress bar.
 *
 * Returned by `GET /v3/carts/{cartId}/commit/status`. The CommitFlow polls this
 * every 2 s while the cart is in a non-terminal commit state.
 */
export interface CommitStatus {
  cartId: string;
  /** Cart-level status mirror (from CartDto.status). */
  cartStatus: CartStatus;
  /** Currently-active step key — null after `committed`/`pipeline-error` settle. */
  currentStepKey?: CommitPipelineStepKey;
  /** All 11 steps with their per-step status. Always returns length=11. */
  steps: ReadonlyArray<CommitPipelineStep>;
  /** Mirrored from CartDto.awaitingSignaturesAt for countdown rendering. */
  awaitingSignaturesAt?: Iso8601;
  /**
   * Awaiting-signatures live signer counts (only present while
   * cartStatus === 'awaiting-signatures'). Drives the AwaitingSignaturesBanner.
   */
  signaturesCollected?: number;
  signaturesTotal?: number;
  /**
   * Configured signing-link expiry in days (default 14 from design §9 row 7).
   * Surfaced so the banner countdown doesn't have to hardcode the policy.
   */
  signingExpiresInDays?: number;
  /** Last error captured at the cart level (mirrors CartDto.lastPipelineErrorMsg). */
  lastPipelineErrorMsg?: string;
  lastPipelineErrorCode?: string;
  /**
   * Completion confirmation record — present once the pipeline finalizes
   * (cartStatus === 'committed'). See CartCompletionSummary.
   */
  completionSummary?: CartCompletionSummary;
  /**
   * REQ-167 — the granular per-call post-sign timeline (one row per Core
   * Director call, from the server's pipeline snapshot). Plain-language
   * labels; account/CIF numbers full; Card PAN pre-masked server-side.
   * Absent until account numbers are allocated (step 3).
   */
  granularSteps?: ReadonlyArray<GranularPipelineStep>;
  /**
   * REQ-167 wedge detection — the server found the cart committed but with
   * unfinished Core Director work (or a missing completion record) and has
   * auto-queued recovery. The FE banner should read "recovering
   * automatically", never demand user action first.
   */
  wedged?: boolean;
  wedgedReason?: string;
  /**
   * CHG-0173 warn mode — plain-language notices that a Core Director change on this
   * application was recorded under the application's officer because the person
   * acting could not be confirmed from their sign-in. Absent when none.
   */
  operatorWarnings?: ReadonlyArray<string>;
  /** Server-side render timestamp — useful for staleness checks in poll loops. */
  fetchedAt?: Iso8601;
}

/** REQ-167 — one Core Director call from the post-sign pipeline snapshot. */
export interface GranularPipelineStep {
  index: number;
  /** Raw snapshot step type (AcctAdd, EFTCardAdd, …) — dev-facing only. */
  type: string;
  /** Plain-language label ("Open account in Core Director", …). */
  plainLabel: string;
  /** pending | done | skipped | error-transient | error-permanent. */
  state: 'pending' | 'done' | 'skipped' | 'error-transient' | 'error-permanent' | string;
  /** Result id (account number / CIF / NT id) — null for card steps. */
  resultId?: string;
  /** SPFx-style result/error detail ("acctId=12345678", "issued new card **** **** **** 1234 …"). */
  detail?: string;
}

/**
 * Outcome envelope returned from POST /v3/carts/{cartId}/commit (and its
 * /retry sibling). The cart row is the canonical post-call state; status is
 * always present so the UI can synchronously react to the transition.
 */
export interface CommitOutcome {
  success: boolean;
  cart?: CartDto;
  /** Snapshot of the live status (also fetchable via GET /commit/status). */
  status?: CommitStatus;
  error?: string;
  /**
   * Closed-list error code surfaced when commit could not start (e.g.
   * `PRECOMMIT_VALIDATION_FAILED`, `LOBBY_TICKET_ACTIVE`,
   * `EXTERNAL_COMMIT_LOCK`). UI maps known codes to inline-error rendering.
   */
  errorCode?: string;
}

/**
 * Body for `POST /v3/carts/{cartId}/commit/abort`.
 * The abort path mirrors the `CloseCartModal` resolution surface, so we
 * reuse the same 6-code taxonomy + error-code pairing.
 */
export interface CommitAbortInput {
  resolutionCode: CartResolutionCode;
  resolutionReason: string;
  /** Required when `resolutionCode === 'System-Error'`. */
  errorCode?: string;
}

/**
 * Resend the SignNow envelope invite for a cart sitting in
 * `awaiting-signatures`. Body is empty — the backend looks up the active
 * `SigningSession` keyed off the cartId.
 *
 * BACKEND CONTRACT ASSUMPTION (Wave 7 frontend agent, 2026-04-25):
 *   Route: POST /v3/carts/{cartId}/signing/resend-invite
 *   Returns: { success, sentTo?, error?, errorCode? }
 * If the backend instead exposes this on `/v3/signing/{cartId}/resend` the
 * client method below is the only place to retarget it.
 */
export interface ResendSigningInviteResponse {
  success: boolean;
  /** Display-safe destination (e.g. "j***@example.com" or "(***) ***-1234"). */
  sentTo?: string;
  error?: string;
  errorCode?: string;
}

/**
 * Map a `CommitPipelineStepKey` to a customer-facing label.
 *
 * Exported as a function (not a static record) so callers always go through
 * the same humanizer; if the design lifts a label later, every surface picks
 * up the change. Never surface the raw key in UI.
 */
export function commitStepLabel(key: CommitPipelineStepKey): string {
  switch (key) {
    case 'pre-commit-validation':
      return 'Verifying cart';
    case 'cif-create':
      return 'Customer record created';
    case 'acct-id-gen':
      return 'Account numbers allocated';
    case 'disclosure-pre-fill':
      return 'Disclosures prepared';
    case 'signing-dispatch':
      return 'Disclosures sent';
    case 'await-signatures':
      // Bill 2026-07-13 Item 22: the step NUMBER prefix ("6 ⏸") read as a
      // signature COUNT next to "Awaiting signatures"; the AwaitingSignatures
      // banner is the single source of the real "N of M collected" count, so
      // the step label is just the action here.
      return 'Signatures';
    case 'acct-add':
      return 'Accounts posted to Core';
    case 'beneficiary-attach':
      return 'Beneficiaries attached';
    case 'post-sign-jx':
      return 'Post-sign provisioning';
    case 'qc-dataverse-write':
      return 'QC writeback';
    case 'notifications':
      return 'Notifications';
    case 'cart-resolution-stamp':
      return 'Resolution';
    case 'account-create':
      return 'Account opened'; // legacy 11-step alias; pre-Wave-0 carts only
    default:
      return key;
  }
}

/**
 * Stable canonical ordering for the 12-step bar (Wave 0).
 * Pre-sign steps 1-6 fire inline in CommitCartAsync; post-sign steps 7-12
 * fire from PipelineResumeFunction once SignNow webhook lands.
 */
export const COMMIT_PIPELINE_STEP_ORDER: ReadonlyArray<CommitPipelineStepKey> = [
  'pre-commit-validation',
  'cif-create',
  'acct-id-gen',
  'disclosure-pre-fill',
  'signing-dispatch',
  'await-signatures',
  'acct-add',
  'beneficiary-attach',
  'post-sign-jx',
  'qc-dataverse-write',
  'notifications',
  'cart-resolution-stamp',
];
